import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import api from '../../config/axios-config'
import { projectQueryKeys } from '../constants'

export const myProjectSortOptions = {
  NAME: 'Name',
  CREATED: 'Created',
  LAST_MODIFIED: 'LastModified',
  COMPANY: 'Company',
  ROLE: 'Role',
  OPEN_TASKS: 'OpenTasks',
  OPEN_PINS: 'OpenPins'
}

export const myProjectSortDirectionOptions = {
  ASC: 'ASC',
  DESC: 'DESC'
}

const normalizeMyProjectsQueryArgs = (queryArgs, paramsArg, optionsArg) => {
  if (queryArgs && typeof queryArgs === 'object' && !Array.isArray(queryArgs)) {
    if ('params' in queryArgs || 'options' in queryArgs) {
      return queryArgs
    }

    return {
      params: queryArgs,
      options: paramsArg ?? {}
    }
  }

  return {
    params: paramsArg ?? {},
    options: optionsArg ?? {}
  }
}

export const getMyProjects = async ({ params = {}, pageParam = 0 }) => {
  const {
    search = '',
    projectSort = myProjectSortOptions.NAME,
    sortDirection = myProjectSortDirectionOptions.ASC,
    size = 15
  } = params

  const config = {
    method: 'get',
    params: {
      search,
      projectSort,
      sortDirection,
      page: pageParam,
      size
    }
  }

  const { data } = await api('/api/projects/paged2', config)

  return data.response ?? data
}

// sc-57356: the backend returns -1 for openPins/openTasks when the counts are
// not cached yet and warms the cache in a background job. While any count on
// the current page is -1 we poll until the real values arrive.
const PENDING_COUNTS_REFETCH_INTERVAL_MS = 30000

const hasPendingCounts = (content = []) =>
  content.some(
    (item) =>
      (item.openPins ?? item.openPinCount) === -1 ||
      (item.openTasks ?? item.openTaskCount) === -1
  )

/**
 * Single-page variant of the my-projects list, shared by the route loader and
 * the table. `useGetMyProjectsQuery` below stays the infinite variant used by
 * the tile and mobile views.
 */
export const myProjectsPageQueryOptions = ({
  search = '',
  projectSort = myProjectSortOptions.LAST_MODIFIED,
  sortDirection = myProjectSortDirectionOptions.DESC,
  page = 0,
  size = 15
} = {}) =>
  queryOptions({
    queryKey: [
      projectQueryKeys.GET_MY_PROJECTS_QUERY,
      'page',
      { search, projectSort, sortDirection, page, size }
    ],
    queryFn: () =>
      getMyProjects({
        params: { search, projectSort, sortDirection, size },
        pageParam: page
      }),
    retry: 0,
    refetchInterval: (query) =>
      hasPendingCounts(query.state.data?.content)
        ? PENDING_COUNTS_REFETCH_INTERVAL_MS
        : false
  })

export const useMyProjectsPageQuery = (params) =>
  useQuery(myProjectsPageQueryOptions(params))

export const useGetMyProjectsQuery = (queryArgs, paramsArg, optionsArg) => {
  const { params = {}, options = {} } = normalizeMyProjectsQueryArgs(
    queryArgs,
    paramsArg,
    optionsArg
  )

  const {
    search = '',
    projectSort = myProjectSortOptions.NAME,
    sortDirection = myProjectSortDirectionOptions.ASC,
    page = 0,
    size = 15
  } = params

  const handleQuery = async ({ pageParam = page }) =>
    getMyProjects({
      params: {
        search,
        projectSort,
        sortDirection,
        size
      },
      pageParam
    })

  return useInfiniteQuery({
    queryKey: [
      projectQueryKeys.GET_MY_PROJECTS_QUERY,
      search,
      projectSort,
      sortDirection,
      page,
      size
    ],
    queryFn: handleQuery,
    retry: 0,
    initialPageParam: page,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      if (!lastPage || lastPage.last === true) {
        return undefined
      }

      return lastPageParam + 1
    },
    ...options
  })
}
